// ════════════════════════════════════════════════════════════════════════════
//  ManDana — the film runtime
//  ----------------------------------------------------------------------------
//  Ninety lines that replace thirteen hundred.
//
//  The landing film was authored in Claude Design against `animations-v3.jsx`,
//  a general animation engine. That engine is an AUTHORING TOOL, and it shows:
//  it renders a playback bar with a scrub track, persists the playhead to
//  localStorage, parses a scene list out of JSON so the host timeline can write
//  trims back into the source file, wraps everything in an <svg><foreignObject>
//  so a video exporter can serialise it, and inlines @font-face rules into that
//  svg so the export renders with the right faces.
//
//  Every one of those is right in a canvas and wrong on a homepage — and the
//  playback bar settles it, because it is VISIBLE. The design file even
//  oversizes its stage by 44px to make room for the thing. "Ship the engine as
//  it is" was never one of the options.
//
//  So the ARTWORK moves across untouched (app/md-droplet.jsx) and the engine
//  underneath it is this file: the same authored clock, the same cue table, the
//  same three motion helpers, and nothing else.
//
//  THE MODEL, unchanged from the original and worth restating because the
//  artwork is written entirely against it: the film is ONE element tree rendered
//  as a pure function of one number, T, in authored seconds. Nothing mounts or
//  unmounts at a movement boundary — every element exists for the whole 57
//  seconds and simply interpolates through them. That is why a droplet can drift
//  out of the abyss, spiral into vapour, ride an orbit and fall to the ground as
//  one continuous thing rather than as nine cuts.
// ════════════════════════════════════════════════════════════════════════════

/** One movement of the film: a named slice of the authored timeline. */
interface MdFilmScene {
  name: string;
  dur: number;
  desc: string;
}

/** Authored start time of each movement, in seconds. */
type MdFilmCueTable = Record<string, number>;

interface MdAnimateSpec {
  from: number;
  to: number;
  start: number;
  end: number;
  ease?: (t: number) => number;
}

interface MdFilmClockSpec {
  total: number;
  /** Reduced motion: hold the last frame from the start. */
  still?: boolean;
}

(function (g: any) {
  'use strict';

  /* The nine movements, exactly as the design file declares them. The order and
     the durations ARE the film's structure — every cue is derived from this list
     and nothing else, so there is one place to change and nothing to keep in
     step. `desc` is the only prose record of what each movement is for; nothing
     reads it at runtime. */
  const MD_FILM_SCENES: MdFilmScene[] = [
    { name: 'Abyss', dur: 7, desc: 'The droplet drifts in the comfortable dark of the deep' },
    { name: 'TheCall', dur: 5, desc: 'A shaft of light finds the droplet and pulls it upward' },
    { name: 'Vapor', dur: 6, desc: 'The droplet breaks into vapor and rises through turbulence' },
    { name: 'Communion', dur: 6, desc: 'Fellow droplets gather and a cloud takes shape' },
    { name: 'Journey', dur: 8, desc: 'Clouds merge and travel over a passing world' },
    { name: 'DeadLand', dur: 5, desc: 'A cracked, hopeless land appears below' },
    { name: 'TheFall', dur: 5, desc: 'The droplet lets go of the sky and dives to the earth' },
    { name: 'Rebirth', dur: 7, desc: 'The broken line heals in light and geometry blooms' },
    { name: 'Finale', dur: 8, desc: 'Total black, then the ManDana logo slowly reveals' },
  ];

  /**
   * Turn the scene list into the cue table the choreography is keyed to.
   *
   * A movement's cue is the running sum of the ones before it — that is the
   * whole rule, and it is computed here rather than written out as constants so
   * that changing a duration cannot leave eight numbers behind disagreeing.
   */
  function mdFilmCues(scenes: MdFilmScene[]): { cues: MdFilmCueTable; total: number } {
    const cues: MdFilmCueTable = {};
    let at = 0;
    for (const scene of scenes) {
      cues[scene.name] = at;
      at += scene.dur;
    }
    return { cues, total: at };
  }

  const mdClamp = (v: number, min: number, max: number): number =>
    (v < min ? min : v > max ? max : v);

  /* The three curves the film actually uses. The original engine shipped
     fifteen; these are the ones the artwork names, and an unused easing
     function is an unused easing function however small. */
  const mdEasing = {
    easeInOutCubic: (t: number): number =>
      (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2),
    easeInCubic: (t: number): number => t * t * t,
    /* Overshoots past 1 and settles back — the rosette's petals bloom on this. */
    easeOutBack: (t: number): number => {
      const c1 = 1.70158;
      const c3 = c1 + 1;
      return 1 + c3 * Math.pow(t - 1, 3) + c1 * Math.pow(t - 1, 2);
    },
  };

  /**
   * A value that moves from `from` to `to` between two authored times, and
   * holds at either end outside that window.
   *
   * Returns a function of T rather than a number, because the artwork builds
   * its ramps once at the top of a render and samples them at the current T —
   * which is what lets one expression describe a value's whole life.
   */
  function mdAnimate(spec: MdAnimateSpec): (t: number) => number {
    const { from, to, start, end } = spec;
    const ease = spec.ease || mdEasing.easeInOutCubic;
    const span = end - start;
    return (t: number): number => {
      if (t <= start) return from;
      if (t >= end) return to;
      // A zero-width window is a hard cut, not a division by zero.
      if (span <= 0) return to;
      return from + (to - from) * ease((t - start) / span);
    };
  }

  /**
   * The authored clock. Plays once from zero and HOLDS at the end — it never
   * loops, because a homepage that restarts a 57-second film behind whatever
   * the visitor is trying to read is asking for attention it has not earned.
   */
  function mdFilmClock(spec: MdFilmClockSpec): { at: (elapsedMs: number) => number } {
    const still = spec.still === true;
    return {
      at: (elapsedMs: number): number =>
        (still ? spec.total : mdClamp(elapsedMs / 1000, 0, spec.total)),
    };
  }

  /**
   * Drive a callback with T until the film reaches its hold, then stop.
   *
   * Stopping matters more than it looks: the held frame is a still image, and a
   * rAF loop that keeps repainting it burns a phone's battery for nothing on a
   * page somebody may leave open. Returns a canceller.
   */
  function mdFilmDrive(spec: MdFilmClockSpec, onFrame: (t: number) => void): () => void {
    const clock = mdFilmClock(spec);

    // Reduced motion: one call with the final frame, and no frames scheduled.
    if (spec.still) {
      onFrame(clock.at(0));
      return () => {};
    }

    let raf = 0;
    let origin: number | null = null;
    let live = true;

    const step = (now: number): void => {
      if (!live) return;
      if (origin === null) origin = now;
      const t = clock.at(now - origin);
      onFrame(t);
      if (t >= spec.total) return;      // the hold — nothing left to draw
      raf = requestAnimationFrame(step);
    };

    raf = requestAnimationFrame(step);
    return () => { live = false; cancelAnimationFrame(raf); };
  }

  Object.assign(g, {
    MD_FILM_SCENES, mdFilmCues, mdAnimate, mdEasing, mdClamp, mdFilmClock, mdFilmDrive,
  });

  const mod = (globalThis as { module?: { exports?: unknown } }).module;
  if (mod && mod.exports) mod.exports = {
    MD_FILM_SCENES, mdFilmCues, mdAnimate, mdEasing, mdClamp, mdFilmClock, mdFilmDrive,
  };
})(typeof window !== 'undefined' ? window : globalThis);
